import useConnectors from "@mirage/service-connectors/useConnectors";
import { useConversationsList } from "@mirage/service-conversation/hooks/useConversationsList";
import { getFirstLoginTime } from "@mirage/service-onboarding";
import { useDismissableModule } from "@mirage/service-onboarding/useDismissableModule";
import { getConnectionMetadata } from "@mirage/shared/connectors/util";
import { UIConnection } from "@mirage/shared/types";
import { ONE_DAY_IN_MILLIS } from "@mirage/shared/util/constants";

import { useEffect, useMemo, useState } from "react";

const MINIMUM_NUMBER_OF_CONVERSATIONS_BEFORE_UPSELL = 5;
const MINIMUM_NUMBER_OF_DAYS_BEFORE_UPSELL = 7;
const DISMISSED_ADD_APPS_UPSELL_IN_CHAT = "dismissedAddAppsUpsellInChat";

export const useAddAppsUpsellInEmbeddedChat = () => {
  const { dismissed, setDismissed } = useDismissableModule(
    DISMISSED_ADD_APPS_UPSELL_IN_CHAT,
  );

  const conversationListState = useConversationsList();
  const { connections, hasFinishedInitialFetch } = useConnectors();

  const [isAfterXDays, setIsAfterXDays] = useState(false);
  useEffect(() => {
    const checkIsAfterXDays = async (numberOfDays: number) => {
      let firstLogin: number | undefined;
      try {
        firstLogin = await getFirstLoginTime();
      } catch (error) {
        // Do nothing if there's an error getting the first login time.
      }

      if (!firstLogin) {
        return;
      }

      if (Date.now() - firstLogin > ONE_DAY_IN_MILLIS * numberOfDays) {
        setIsAfterXDays(true);
      }
    };
    checkIsAfterXDays(MINIMUM_NUMBER_OF_DAYS_BEFORE_UPSELL);
  }, [MINIMUM_NUMBER_OF_DAYS_BEFORE_UPSELL, getFirstLoginTime]);

  const hasAtLeastXConversations =
    conversationListState.type === "loaded" &&
    conversationListState.conversationMetadatas.length >=
      MINIMUM_NUMBER_OF_CONVERSATIONS_BEFORE_UPSELL;

  // Count individual apps only (exclude team/admin apps, and browser extension)
  const individualConnections = useMemo(() => {
    return connections.filter((connection) => {
      const { teamConnector } = getConnectionMetadata(
        connection as UIConnection,
      );
      return (
        !teamConnector &&
        connection.id_attributes?.connector?.type !== "browser_extension"
      );
    });
  }, [connections]);
  const hasIndividualConnection = individualConnections.length > 0;

  // Upsell if user has no individual connections and
  // has had at least 5 conversations or using Dash for at least 7 days.
  const canUpsell =
    !hasIndividualConnection && (hasAtLeastXConversations || isAfterXDays);

  // Don't show upsell if connections are still loading or if user has already dismissed the upsell.
  if (!hasFinishedInitialFetch || !canUpsell || dismissed) {
    return {
      showAddAppsUpsell: false,
      isLoading: !hasFinishedInitialFetch,
      onDismiss: () => {},
    };
  }

  return {
    showAddAppsUpsell: true,
    onDismiss: () => setDismissed(true),
    isLoading: !hasFinishedInitialFetch,
  };
};
